import { useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { cn } from '../../shared/utils/cn'
import { DateRangePreset } from '../../utils/csvExportTypes'

interface ExportDateRangeProps {
  from: number  // Unix timestamp in milliseconds
  to: number    // Unix timestamp in milliseconds
  onChange: (from: number, to: number) => void
  maxDate?: Date
}

const PRESETS: { key: DateRangePreset; label: string; days: number }[] = [
  { key: '1d', label: '1d', days: 1 },
  { key: '7d', label: '7d', days: 7 },
  { key: '28d', label: '28d', days: 28 },
]

export function ExportDateRange({
  from,
  to,
  onChange,
  maxDate = new Date()
}: ExportDateRangeProps) {
  const maxTimestamp = maxDate.getTime()

  // Determine active preset based on current date range
  const activePreset = useMemo((): DateRangePreset | null => {
    const days = Math.round((to - from) / (24 * 60 * 60 * 1000))

    // Check if "to" is near current date (within 1 day)
    const isNearCurrent = Math.abs(to - maxTimestamp) < 24 * 60 * 60 * 1000

    if (!isNearCurrent) return null

    for (const preset of PRESETS) {
      if (days === preset.days) {
        return preset.key
      }
    }
    return null
  }, [from, to, maxTimestamp])

  const handlePresetClick = (preset: { key: DateRangePreset; days: number }) => {
    const now = new Date()
    let newFrom: Date
    let newTo: Date

    if (preset.days === 1) {
      // For "1d", use current day from 00:00:00 to 23:59:59
      newFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      newTo = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
    } else {
      // For week and month presets
      newTo = now
      newFrom = new Date(now.getTime() - preset.days * 24 * 60 * 60 * 1000)
    }

    onChange(newFrom.getTime(), newTo.getTime())
  }

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = new Date(event.target.value)
    selectedDate.setHours(0, 0, 0, 0)
    const newFrom = selectedDate.getTime()

    // If end date is older than new start date, set end date to start date
    const newTo = newFrom > to ? newFrom : to
    onChange(newFrom, newTo)
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = new Date(event.target.value)
    selectedDate.setHours(23, 59, 59, 999)
    let newTo = selectedDate.getTime()

    // Cap at max date
    if (newTo > maxTimestamp) {
      newTo = maxTimestamp
    }

    // If start date is newer than new end date, set start date to end date
    const newFrom = newTo < from ? newTo : from
    onChange(newFrom, newTo)
  }

  const formatDateForInput = (timestamp: number): string => {
    return new Date(timestamp).toISOString().split('T')[0]
  }

  // Calculate range duration for shifting
  const rangeDuration = to - from

  const handleShiftLeft = () => {
    const newFrom = from - rangeDuration
    const newTo = to - rangeDuration
    onChange(newFrom, newTo)
  }

  const handleShiftRight = () => {
    let newTo = to + rangeDuration
    // Cap at max date
    if (newTo > maxTimestamp) {
      newTo = maxTimestamp
    }
    const newFrom = newTo - rangeDuration
    onChange(newFrom, newTo)
  }

  // Can shift right only if we're not already at max date
  const canShiftRight = to < maxTimestamp - 60000 // 1 minute buffer

  const maxDateString = maxDate.toISOString().split('T')[0]

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        Date Range
      </label>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Preset Buttons */}
        <div className="flex items-center gap-1">
          {PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => handlePresetClick(preset)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                activePreset === preset.key
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="w-px h-6 bg-gray-200 mx-1" />

        {/* Date Pickers with Chevrons */}
        <div className="flex items-center gap-1">
          {/* Left Chevron */}
          <button
            type="button"
            onClick={handleShiftLeft}
            className={cn(
              'p-1.5 rounded transition-colors',
              'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            )}
            title="Shift range earlier"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
          </button>

          <input
            type="date"
            value={formatDateForInput(from)}
            onChange={handleStartDateChange}
            max={formatDateForInput(to)}
            className={cn(
              'px-2 py-1.5',
              'text-sm border border-gray-200 rounded',
              'bg-white text-gray-900',
              'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
              'cursor-pointer'
            )}
            style={{ colorScheme: 'light' }}
          />

          <span className="text-gray-400 text-sm">→</span>

          <input
            type="date"
            value={formatDateForInput(to)}
            onChange={handleEndDateChange}
            min={formatDateForInput(from)}
            max={maxDateString}
            className={cn(
              'px-2 py-1.5',
              'text-sm border border-gray-200 rounded',
              'bg-white text-gray-900',
              'focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
              'cursor-pointer'
            )}
            style={{ colorScheme: 'light' }}
          />

          {/* Right Chevron */}
          <button
            type="button"
            onClick={handleShiftRight}
            disabled={!canShiftRight}
            className={cn(
              'p-1.5 rounded transition-colors',
              canShiftRight
                ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                : 'text-gray-200 cursor-not-allowed'
            )}
            title="Shift range later"
          >
            <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  )
}
